<script>
    import AssetsApi from "../../../asset/assets.api.js";
    import tagService from "../../../tag/service.js";
    import Spinner from "../../../../components/spinner.svelte";
    import BreadCrumbNav from "../../../../components/breadcrum-nav.svelte";
    import PropValueRow from "../../../../components/powerTable/prop-value-row.svelte";
    import PropNameRow from "../../../../components/powerTable/prop-name-row.svelte";
    import PropLocRow from "../../../../components/powerTable/prop-loc-row.svelte";
    import CountByPropValue from "../../_components/count-by-prop-value.svelte";
    import { assets_kinds } from "../../../asset/assets.api.js";
    import { format, locale } from "svelte-i18n";
    import { token } from "../../../user/user.state.svelte.js";
    import { params, goto } from "@roxi/routify";
    import { PowerTable } from "@muonw/powertable";

    // --- Vanilla JS Replacements ---
    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";

    const redirect = $goto;

    let kind = $derived($params.kind ? decodeURIComponent($params.kind) : "");
    let tableElement = $state();

    // Active filters driven strictly by the URL
    let currentFilter = $derived.by(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));
        const active = {};
        if (paramsCopy.asset__kind__in) active.asset__kind__in = paramsCopy.asset__kind__in;
        if (paramsCopy.asset_tag_id) active.asset_tag_id = paramsCopy.asset_tag_id;
        return active;
    });

    // SVELTE 5: Local UI state for form inputs before user clicks "Search"
    let filters = $state({
        asset__kind__in: [],
        asset_tag_id: ""
    });

    // Localize asset kinds for TomSelect
    let translatedAssetKinds = $derived(
        assets_kinds.map((opt) => ({
            id: opt.id,
            name: $format(`asset.kind.${opt.id}.singular`)
        }))
    );

    // Sync URL $params to local UI state
    $effect(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));

        let initialKind = paramsCopy.asset__kind__in || [];
        if (typeof initialKind === "string") {
            initialKind = initialKind.split(",");
        }

        filters = {
            asset__kind__in: initialKind,
            asset_tag_id: paramsCopy.asset_tag_id || ""
        };
    });

    // --- TomSelect Svelte Action ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return {
                        id: opt.id,
                        name: opt.name || opt.title_es || opt.title || opt.email || opt.id
                    };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || "Buscar...",
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            }
        };
    }

    const getTags = async () => await tagService.search("v2/tag/");

    const handleEmptyFilters = (filtersObj = {}) => {
        const clean = {};
        for (let [key, val] of Object.entries(filtersObj)) {
            if (val !== "" && val !== null && val !== undefined) {
                clean[key] = val;
            }
        }
        return clean;
    };

    function handleFilterChange() {
        let updatedFilter = {};

        if (filters.asset__kind__in && filters.asset__kind__in.length > 0) {
            updatedFilter.asset__kind__in = filters.asset__kind__in.join(",");
        }

        if (filters.asset_tag_id) {
            updatedFilter.asset_tag_id = filters.asset_tag_id;
        }

        const cleanFilters = handleEmptyFilters(updatedFilter);
        redirect("./", { kind: kind, ...cleanFilters });
    }

    const clearFilters = () => {
        if (!currentFilter.asset__kind__in && !currentFilter.asset_tag_id) {
            return;
        }

        filters = { asset__kind__in: [], asset_tag_id: "" };
        redirect("./", { kind: kind });
    };

    const parseRows = (pageContent) => {
        const cleanFilters = handleEmptyFilters(currentFilter);

        pageContent.forEach((row) => {
            if (row.name) {
                row.name = {
                    id: row.id,
                    name: row.name,
                    kind: row.kind,
                    filters: { ...cleanFilters },
                };
                if (row.asset_id) row.name.target_object_type = "asset";
                if (row.finding_id) row.name.target_object_type = "finding";
                if (row.vulnerability_id) row.name.target_object_type = "vulnerability";
            }
            if (row["location"]) {
                row["location"] = {
                    kind: row.kind,
                    location: row.location,
                    target_object_type: "asset",
                    filters: { ...cleanFilters },
                };
            }
            if (row["asset_name"]) {
                row["asset_name"] = `<a href='/asset/${row.asset_kind}/${row["asset_id"]}' title="Ver">${row.asset_name}</a>`;
            }
            if (row["asset_kind"]) {
                row["asset_kind"] = $format(`asset.kind.${row.asset_kind}.singular`);
            }
            if (row.hasOwnProperty("value")) {
                const propName = typeof row.name === "object" ? row.name.name : row.name;
                if (row.value) {
                    row.value = {
                        kind: kind,
                        name: propName,
                        value: row.value,
                        count: row.count,
                        target_object_type: "asset",
                        filters: { ...cleanFilters },
                    };
                } else {
                    row.value = {
                        kind: kind,
                        name: propName,
                        value: null,
                        count: row.count,
                        target_object_type: "asset",
                        filters: { ...cleanFilters },
                    };
                }
            }
        });
        return pageContent;
    };

    let ptOptions = $state({
        uniquePrefix: "assetKindPropTable",
        rowsPerPageOptions: [5, 10, 20, 50, 100],
        rowsPerPage: 10,
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {
            add: $format("list.interface.add"),
            delete: $format("list.interface.delete"),
            filterBy: $format("list.interface.filterBy"),
            from: $format("list.interface.from"),
            invertSelection: $format("list.interface.invertSelection"),
            next: $format("list.interface.next"),
            of: $format("list.interface.of"),
            previous: $format("list.interface.previous"),
            rows: $format("list.interface.rows"),
            search: $format("list.interface.search"),
            selectAll: $format("list.interface.selectAll"),
            selectNone: $format("list.interface.selectNone"),
        },
        isDataRemote: false,
        userFunctions: {
            customParse: parseRows,
        },
    });

    let ptInstructs = [
        {
            key: "name",
            title: $format("list.columns.name"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropNameRow,
        },
        {
            key: "location",
            title: $format("list.columns.location"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropLocRow,
        },
        {
            key: "asset_name",
            title: $format("list.columns.asset"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "asset_kind",
            title: $format("list.columns.asset_kind"),
            filterable: false,
        },
        {
            key: "value",
            title: $format("list.columns.value"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropValueRow,
        },
    ];

    const getProps = async (kindParam) => {
        if (!token.access_token) return {};

        const cleanFilters = handleEmptyFilters({ ...currentFilter, kind: kindParam });
        let properties = await AssetsApi.fetchAssets("v2/asset-property/", {
            filter: cleanFilters,
        });

        let propsByName = groupPropsByName(JSON.parse(JSON.stringify(properties || [])));
        let propsByNameAndValue = groupPropsByNameAndValue(JSON.parse(JSON.stringify(properties || [])));
        let data = JSON.parse(JSON.stringify(properties || []));

        data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        let assets = await getAssets();

        for (let item of data) {
            let asset = assets.find((asset) => asset.id === item.asset_id);
            if (asset) {
                item.asset_kind = asset.kind;
                item.asset_name = asset.name;
            }
            item.location =
                item.location && item.location !== ""
                    ? item.location
                    : "General";
        }

        return {
            data: data,
            propsByName: propsByName,
            propsByNameAndValue: propsByNameAndValue,
        };
    };

    const getAssets = async () => {
        if (!token.access_token) return [];
        let filterObj = {};
        if (currentFilter.asset__kind__in) {
            filterObj.kind__in = currentFilter.asset__kind__in;
        }
        if (currentFilter.asset_tag_id) {
            filterObj.tag_id = currentFilter.asset_tag_id;
        }
        filterObj = handleEmptyFilters(filterObj);
        let assets = await AssetsApi.fetchAssets("v2/asset/", {
            filter: { ...filterObj, property_kind: kind },
        });
        return assets;
    };

    const groupPropsByName = (properties = []) => {
        let groupedProps = properties.reduce((a, b) => {
            const found = a.find((e) => e.name === b.name);
            if (found) {
                found.asset_id.push(b.asset_id);
            } else {
                a.push({ ...b, asset_id: [b.asset_id] });
            }
            return a;
        }, []);
        for (let prop of groupedProps) {
            let count = new Set(prop.asset_id);
            prop.asset_id = Array.from(count);
            prop.count = prop.asset_id.length;
        }
        return groupedProps;
    };

    const groupPropsByNameAndValue = (properties = []) => {
        let groupedProps = properties.reduce((a, b) => {
            const found = a.find((e) => e.value === b.value && e.name === b.name);
            if (found) {
                found.asset_id.push(b.asset_id);
            } else {
                a.push({ ...b, asset_id: [b.asset_id] });
            }
            return a;
        }, []);
        for (let prop of groupedProps) {
            let count = new Set(prop.asset_id);
            prop.asset_id = Array.from(count);
            prop.count = prop.asset_id.length;
            prop.label = prop.name + " - " + prop.value;
        }
        return groupedProps;
    };
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">{kind}</h1>
            </div>
            <div class="col-6">
                <BreadCrumbNav
                    routes={[
                        {
                            label: $format("page.properties.asset_props"),
                            path: "/property/asset",
                        },
                        { label: kind },
                    ]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="card mt-3 card-secondary card-outline no-export">
        <div class="card-header">
            <h4 class="card-title">
                {$format("components.advancedSearch.search")}
            </h4>
        </div>
        <div class="card-body pe-5">
            <div class="row">
                <div class="col-12 col-md-6 mb-3">
                    <label for="asset__kind__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                        {$format("components.advancedSearch.asset_kind")}
                    </label>

                    <!-- SVELTE 5 UPDATE: Replaced Select2 with TomSelect -->
                    <select
                        id="asset__kind__in"
                        name="asset__kind__in"
                        multiple
                        use:tomSelectAction={{
                            options: translatedAssetKinds,
                            value: filters.asset__kind__in,
                            placeholder: $format("buttons.search"),
                            onChange: (val) => (filters.asset__kind__in = val),
                        }}
                    ></select>
                </div>

                <div class="col-12 col-md-6 mb-3">
                    {#await getTags()}
                        <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                            {$format("components.advancedSearch.asset_tag")}
                        </label>
                        <select class="form-select" name="asset_tag_id" disabled>
                            <option value="">--</option>
                        </select>
                    {:then tags}
                        <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                            {$format("components.advancedSearch.asset_tag")}
                        </label>

                        <!-- SVELTE 5 UPDATE: Standard BS5 select -->
                        <select
                            class="form-select"
                            name="asset_tag_id"
                            bind:value={filters.asset_tag_id}
                        >
                            <option value="">--</option>
                            {#each tags as opt}
                                <option value={opt.id}>
                                    {opt.name}
                                </option>
                            {/each}
                        </select>
                    {/await}
                </div>
            </div>

            <div class="btn-strip pt-3 mt-2 border-top">
                <button class="btn btn-primary btn-sm px-4 me-2" onclick={handleFilterChange}>
                    <i class="fas fa-search me-1"></i> {$format("buttons.search")}
                </button>
                <button class="btn btn-light btn-sm px-4 border" onclick={clearFilters}>
                    <i class="fas fa-eraser me-1"></i> {$format("buttons.reset")}
                </button>
            </div>
        </div>
    </div>

    {#await getProps(kind)}
        <div class="row">
            <div class="col-12 d-flex">
                <div class="card mt-3 w-100">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("properties")}
                        </div>
                    </div>
                    <div class="card-body d-flex justify-content-center">
                        <Spinner />
                    </div>
                </div>
            </div>
        </div>
    {:then data}
        <div class="row">
            <div class="col-12 d-flex flex-wrap flex-md-nowrap" style="gap: 15px;">
                <div class="card mt-3 w-100">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("page.properties.assets_per_prop_name")}
                        </div>
                    </div>
                    <div class="card-body">
                        <CountByPropValue
                            data={data.propsByName}
                            field={"name"}
                            type={"asset"}
                            propertyKind={kind}
                            linkTo={"name"}
                            filter={currentFilter}
                        />
                    </div>
                </div>
                <div class="card mt-3 w-100">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("page.properties.assets_per_prop_name_and_value")}
                        </div>
                    </div>
                    <div class="card-body">
                        <CountByPropValue
                            data={data.propsByNameAndValue}
                            type={"asset"}
                            propertyKind={kind}
                            linkTo={"values"}
                            filter={currentFilter}
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <div class="card mt-3">
                    <div class="card-header">
                        <div class="card-title">{$format("properties")}</div>
                    </div>
                    <!-- Trigger table re-render when row count changes -->
                    {#key ptOptions.rowsPerPage}
                        <div class="card-body">
                            <div class="table-responsive">
                                <PowerTable
                                    ptData={data.data}
                                    {ptInstructs}
                                    {ptOptions}
                                    bind:this={tableElement}
                                />
                            </div>
                        </div>
                    {/key}
                </div>
            </div>
        </div>
    {/await}
</section>

<style>
    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }
</style>